// hooks/useMultiCarManagement.tsx
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { MultiCarItem, OrderItem } from "../../home/components/types";
import { addProduct, createCar, removeProduct } from "../utils/multiCar.utils";
import { useCarWashData } from "./useCarWashData";

export const useMultiCarManagement = (showModalOnInit: boolean = false) => {
  // Simple initialization - always load from localStorage
  const [multiCar, setMultiCar] = useState<MultiCarItem[]>(() => {
    // Check for special flags first
  
    
    // Otherwise load saved data
    const saved = localStorage.getItem("carWashMultiCar");
    return saved ? JSON.parse(saved) : [];
  });
  const {customerData}=useCarWashData()

  const [carNumber, setCarNumber] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(showModalOnInit);
  const [editCarIndex, setEditCarIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [shouldPreserveData, setShouldPreserveData] = useState<boolean>(false);

  // Save to localStorage whenever multiCar changes
  // useEffect(() => {
  //   if (multiCar.length > 0) {
  //     localStorage.setItem("carWashMultiCar", JSON.stringify(multiCar));
  //   } else {
  //     localStorage.removeItem("carWashMultiCar");
  //   }
  // }, [multiCar]);

  // Check on mount if we need to preserve data (coming back from payment)
  
  useEffect(() => {
    const isFromPayment = sessionStorage.getItem("carWashFromPayment") === "true";
    
    if (isFromPayment) {
      // Coming back from payment - preserve data
      setShouldPreserveData(true);
      sessionStorage.removeItem("carWashFromPayment");
    }
  }, []);

  const addCar = () => {
    const trimmedCarNumber = carNumber.trim().toUpperCase();

    if (!trimmedCarNumber) {
      toast.error("Please enter a valid car number.");
      return;
    }

    const isDuplicate = multiCar.some((car) => car.carNumber?.toUpperCase() === trimmedCarNumber);

    if (isDuplicate) {
      toast.error("This car number is already added.");
      return;
    }

    setMultiCar((prev) => [...prev, createCar(trimmedCarNumber)]);
    setCarNumber("");
    setIsModalOpen(false);
    setEditCarIndex(null);
  };

  useEffect(() => {
    console.log(customerData)
    customerData?.serviceDetails?.map((car)=>{
      setMultiCar((prev) => [...prev, car]);
    })
    
  },[customerData])



  /** Remove entire car */
  const removeCar = (carIndex: number) => {
    setMultiCar((prev) => prev.filter((_, index) => index !== carIndex));
  };

  const removeProductFromCar = (productToDelete: { product: string; price: string } | null, carIndex: number) => {
    setMultiCar((prev) => {
      const updatedCars = [...prev];
      const targetCar = updatedCars[carIndex];

      if (!targetCar) return prev;

      const updatedCar = {
        ...targetCar,
        productDetails: targetCar.productDetails.filter((product) => !(product.product === productToDelete?.product && product.price === productToDelete?.price)),
        totalPrice: targetCar.productDetails
          .filter((product) => !(product.product === productToDelete?.product && product.price === productToDelete?.price))
          .reduce((sum, product) => sum + parseFloat(product.price || "0"), 0),
      };

      updatedCars[carIndex] = updatedCar;
      return updatedCars;
    });
  };

  /** Add product to a car */
  const addProductToCar = (product: OrderItem, carIndex: number | null = null) => {
    setMultiCar((prev) => {
      const updatedCars = [...prev];
      const targetIndex = carIndex ?? updatedCars.length - 1;

      if (targetIndex < 0 || targetIndex >= updatedCars.length) {
        toast.warning("Please add a car first.");
        return prev;
      }

      const updatedCar = addProduct(updatedCars[targetIndex], product);
      if (!updatedCar) return prev;

      updatedCars[targetIndex] = updatedCar;
      return updatedCars;
    });
  };

  /** Calculate total price */
  const calculateTotalPrice = () => multiCar.reduce((acc, car) => acc + car.totalPrice, 0).toFixed(2);

  /** Clear all data */
  const clearAllData = useCallback(() => {
    setMultiCar([]);
    setCarNumber("");
    setEditCarIndex(null);
    setIsEditing(false);
    setShouldPreserveData(false);
    
    localStorage.removeItem("carWashMultiCar");
    localStorage.removeItem("carWashPaymentMethod");
    
    console.log("Car wash data cleared");
  }, []);

  /** Clear data only for printing */
  const clearDataForPrint = useCallback(() => {
    sessionStorage.setItem("carWashPrintCompleted", "true");
    clearAllData();
  }, [clearAllData]);

  /** Clear data for dashboard/order navigation */
  const clearDataForNavigationAway = useCallback(() => {
    sessionStorage.setItem("carWashFromDashboard", "true");
    clearAllData();
  }, [clearAllData]);

  /** Mark that we're going to payment page (to preserve data on back) */
  const markGoingToPayment = useCallback(() => {
    sessionStorage.setItem("carWashFromPayment", "true");
  }, []);

  /** Clear data when navigating away to dashboard/order */
  const handleNavigateAway = useCallback(() => {
    if (multiCar.length > 0) {
      clearDataForNavigationAway();
    }
  }, [multiCar.length, clearDataForNavigationAway]);

  /** Editing controls */
  const startEditingCar = (index: number) => {
    setEditCarIndex(index);
    setIsEditing(true);
  };

  const stopEditingCar = () => {
    setEditCarIndex(null);
    setIsEditing(false);
  };

  return {
    multiCar,
    carNumber,
    setCarNumber,
    isModalOpen,
    setIsModalOpen,
    editCarIndex,
    isEditing,
    addCar,
    removeCar,
    removeProductFromCar,
    addProductToCar,
    calculateTotalPrice,
    startEditingCar,
    stopEditingCar,
    clearAllData,
    clearDataForPrint,
    clearDataForNavigationAway,
    markGoingToPayment,
    handleNavigateAway,
    shouldPreserveData,
  };
};